// The RBP Shopping List (`/rbp`): the programme header in place of the depot shopping navigation
// (RBP-D39), the totals and details strip (RBP-D18) and the benefit group rows (RBP-D38). The page
// spans the five RBP depots, so it needs no ordering choice: SL1.0 opens it from the depot chooser
// (`choose.htm:23`) before any depot is chosen.
//
// States: loading; a failure with a retry; "no list for this store" when the store does not take
// part (the 404); and the list, whose frame is drawn with blank values when no report is current
// yet (the 204).
//
// Each group row carries SL1.0's ellipsis menu with "Clear selection" (the group's selections; SL1.0's
// "Deselect", renamed with the check marks gone, ADO-4037), and the totals strip one for every
// selection. Clearing is destructive and cannot be undone, so both ask first on the design-system
// `Modal`, as the Re-add band does (`Orders/ReAddOrder.tsx`): only the confirm sends the DELETE,
// which then keeps its asynchronous discipline, re-reads the header and the rows and closes the
// open group. A stored quantity re-reads them too.
//
// Each menu leads with "Add to cart" and follows it with "Clear selection", so the destructive
// action is never the first thing under the cursor. "Add to cart" and the header's "Add selected to
// cart" (every selection) start SL1.0's add (`rbp_report.htm:133-141`,
// `rbp_report_header.htm:66-72`, `nav.php:307-324`): the confirmation per depot, then the add.
// Nothing to add is a banner under the totals, with no dialog. After an add the list is read again
// and the open group closes, as SL1.0 reloads the page. The add never changes the active depot.
import { useCallback, useRef, useState } from 'react';
import { deselectRbpAll, deselectRbpGroup, type RbpBenefitGroupRow } from '../../api/rbp.client';
import { Banner, Button, Modal, useHeaderLayout } from '../../design-system';
import { createRbpPendingSelections } from '../../rbp/rbpPendingSelections';
import { useRbpCartAddition } from '../../rbp/useRbpCartAddition';
import { useRbpShoppingList } from '../../rbp/useRbpShoppingList';
import { BenefitGroupMenu } from './BenefitGroupMenu';
import { BenefitGroupTable } from './BenefitGroupTable';
import { RbpCartAdditionDialog } from './RbpCartAdditionDialog';
import { RbpProgrammeHeader } from './RbpProgrammeHeader';
import { RbpTotalsStrip } from './RbpTotalsStrip';
import styles from './RbpShoppingListPage.module.css';

/** The clear a shopper asked for and the dialog is holding until they confirm it. */
type PendingClear =
  | { readonly scope: 'all' }
  | { readonly scope: 'group'; readonly benefitGroupCode: string; readonly description: string };

export function RbpShoppingListPage() {
  const list = useRbpShoppingList();
  const header = list.status === 'ready' ? list.header : null;
  const { refresh } = list;
  const [collapseSignal, setCollapseSignal] = useState(0);
  const [deselectFailed, setDeselectFailed] = useState(false);
  const [pendingClear, setPendingClear] = useState<PendingClear | null>(null);
  // Shared across the open group's line selections, so a Deselect can let their in-flight writes
  // settle before its DELETE: no PUT can then reach the server after the DELETE and re-create a row.
  const pending = useRef(createRbpPendingSelections()).current;

  // Declared before the deselect so a change to the selections can clear a stale notice.
  const afterAdd = useCallback(() => {
    refresh();
    setCollapseSignal((count) => count + 1);
  }, [refresh]);
  const addition = useRbpCartAddition(afterAdd);
  const { clearNotice } = addition;
  const selectionSaved = useCallback(() => {
    clearNotice();
    refresh();
  }, [clearNotice, refresh]);

  const deselect = useCallback(
    async (clear: () => Promise<void>) => {
      clearNotice();
      setDeselectFailed(false);
      // Latch synchronously, before any await: a step in the same tick as the confirm, or while it
      // waits for the in-flight writes and the DELETE, must not start a PUT that lands after it.
      pending.beginClear();
      try {
        await pending.settled();
        await clear();
      } catch {
        setDeselectFailed(true);
      } finally {
        pending.endClear();
      }
      refresh();
      setCollapseSignal((count) => count + 1);
    },
    [clearNotice, pending, refresh],
  );
  const confirmClear = useCallback(() => {
    if (pendingClear === null) return;
    const clear =
      pendingClear.scope === 'all'
        ? deselectRbpAll
        : () => deselectRbpGroup(pendingClear.benefitGroupCode);
    setPendingClear(null);
    // Called straight from the click, so the latch inside still happens in this tick.
    void deselect(clear);
  }, [deselect, pendingClear]);
  const addBusy = addition.state.status === 'previewing' || addition.state.status === 'adding';
  const { start: startAddition } = addition;
  const addAll = useCallback(() => startAddition({ benefitGroupCode: null }), [startAddition]);
  const groupMenu = (row: RbpBenefitGroupRow) => (
    <BenefitGroupMenu
      label={`Actions for ${row.description}`}
      items={[
        {
          key: 'add-to-cart',
          label: 'Add to cart',
          onSelect: () => startAddition({ benefitGroupCode: row.benefitGroupCode }),
        },
        {
          key: 'deselect',
          label: 'Clear selection',
          onSelect: () =>
            setPendingClear({
              scope: 'group',
              benefitGroupCode: row.benefitGroupCode,
              description: row.description,
            }),
        },
      ]}
    />
  );

  return (
    <div className={styles.page}>
      <RbpProgrammeHeader
        quarterClosesOn={header?.quarterClosesOn ?? null}
        onAddSelected={list.status === 'ready' ? addAll : undefined}
        addBusy={addBusy}
      />
      <main className={styles.main}>
        <h1 className={styles.heading}>RBP Shopping List</h1>
        {list.status === 'loading' && (
          <p role="status" className={styles.status}>
            Loading your RBP Shopping List…
          </p>
        )}
        {list.status === 'error' && (
          <div className={styles.notice}>
            <Banner type="error">
              We couldn&rsquo;t load your RBP Shopping List. Please try again.
            </Banner>
            <Button variant="secondary" onClick={list.retry}>
              Try again
            </Button>
          </div>
        )}
        {list.status === 'not-a-participant' && (
          <Banner type="info">
            Your store does not take part in the Retailer Benefit Programme.
          </Banner>
        )}
        {list.status === 'ready' && (
          <>
            {deselectFailed ? (
              <Banner type="error">
                We couldn&rsquo;t clear your selection. Please try again.
              </Banner>
            ) : null}
            <RbpTotalsStrip
              header={list.header}
              onDeselectAll={() => setPendingClear({ scope: 'all' })}
              onAddAll={addAll}
            />
            {addition.state.status === 'nothing-to-add' ? (
              <Banner type="info">No products to add!</Banner>
            ) : null}
            {addition.state.status === 'failed' ? (
              <Banner type="error">{addition.state.message}</Banner>
            ) : null}
            <BenefitGroupTable
              rows={list.groups}
              renderRowExtra={groupMenu}
              onSelectionSaved={selectionSaved}
              collapseSignal={collapseSignal}
              pending={pending}
            />
          </>
        )}
      </main>
      <ClearSelectionDialog
        pending={pendingClear}
        onConfirm={confirmClear}
        onCancel={() => setPendingClear(null)}
      />
      <RbpCartAdditionDialog
        state={addition.state}
        onConfirm={addition.confirm}
        onClose={addition.dismiss}
      />
    </div>
  );
}

/**
 * The confirmation a clear-selection action asks before anything is deleted. Named after the
 * destructive action and its scope, so the all-store clear is never mistaken for a group one.
 * Cancel, Escape, the close button and the scrim all leave the selections alone. On mobile it is
 * the bottom sheet with the confirm on top, as the Re-add confirmations are (ADO-1779).
 */
function ClearSelectionDialog({
  pending,
  onConfirm,
  onCancel,
}: {
  readonly pending: PendingClear | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  const mobile = useHeaderLayout() === 'mobile';
  const placement = mobile
    ? ({ placement: 'bottom', sheetGutter: 'compact' } as const)
    : ({ placement: 'center' } as const);
  const group = pending !== null && pending.scope === 'group' ? pending.description : null;
  const cancel = (
    <Button key="cancel" variant="secondary" onClick={onCancel}>
      Cancel
    </Button>
  );
  const confirm = (
    <Button key="confirm" variant="destructive" onClick={onConfirm}>
      Clear selection
    </Button>
  );

  return (
    <Modal
      open={pending !== null}
      onClose={onCancel}
      {...placement}
      title={group === null ? 'Clear your selection?' : `Clear selection for ${group}?`}
      footer={<>{mobile ? [confirm, cancel] : [cancel, confirm]}</>}
    >
      <p className={styles.confirmBody}>
        {group === null
          ? "Are you sure you want to clear your selection? This can't be undone."
          : `Are you sure you want to clear the selection for ${group}? This can't be undone.`}
      </p>
    </Modal>
  );
}
